// Helpers for the RFA/RFI/Submittal/ProjectOpenItem PDF download flow.
//
// Why these exist and why they look unusual:
// =========================================
//
// The companion spackle-api PR (#3152) introduces four GET endpoints
// (`pdf_document_download_url`) that return a presigned S3 URL with a
// `Content-Disposition: attachment; filename=...` header. The browser needs
// that header on a cross-origin response to honour the `download` attribute
// on the anchor element — without it, the file opens or navigates instead of
// saving (which is the bug this PR fixes).
//
// The web PR merges to `develop`, which deploys staging immediately. If the
// API PR is not yet deployed to staging, those routes 404 with Rails' default
// router 404 (an HTML or empty body with **no `errors` key**). Every Download
// button would then show an error toast instead of downloading. The fallback
// below makes that impossible: if the route is genuinely absent we fall back
// to the pre-existing direct S3 URL, which at worst opens in a new tab —
// strictly better than the error toast.
//
// THE TRAP WITH THIS INTERCEPTOR
// ------------------------------
// `src/services/api/api-configuration.js` installs an axios response
// interceptor that, on the rejection path, does:
//
//     if (!isBlobBody && (!errorData || !errorData.hasOwnProperty('errors'))) {
//       newError = {
//         ...error,
//         response: {
//           data: { errors: ['An unexpected error occurred.'] },
//         },
//       };
//     }
//
// The catch: a Rails router 404 has no `errors` key in its body, so the
// interceptor REPLACES `error.response` with an object that has ONLY `data`
// — `status` is GONE, and an `errors` key is now PRESENT. A naive
// discrimination like "404 with no errors key = endpoint absent" is exactly
// backwards by the time `.catch` runs: the interceptor has fabricated the
// `errors` key.
//
// THE FIX
// =======
// Keep the 404 out of the error path entirely, using axios's per-request
// `validateStatus` (supported in this repo's axios 0.21.4; not currently used
// elsewhere in src/, so this is an introduction). A resolved 404 never
// reaches the interceptor's error branch, so `response.status` survives and
// the discrimination below is honest:
//
//   - 404 with NO errors body  -> route absent -> fall back to legacy S3 URL
//   - 404 WITH errors body     -> document genuinely missing -> show error
//   - anything else (2xx)      -> use the returned presigned URL
//   - anything thrown          -> NEVER endpoint-absent (could be 401/403)
//
// `pdfDownloadFailureFrom` always returns `endpointAbsent: false` for the
// rejection path. A 401/403 is a real refusal, and because the interceptor
// strips `status` from any body lacking an `errors` key, a status-stripped
// 403 is indistinguishable from a transport failure — falling back on that
// ambiguity could silently bypass a refusal, which is a security regression.
// See `tests/pdf-download-fallback.test.mjs` for the case table.

export const ALLOW_404 = {
  validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
};

export function pdfDownloadResultFrom(response) {
  if (response.status === 404) {
    const errors =
      response.data &&
      Object.prototype.hasOwnProperty.call(response.data, 'errors')
        ? response.data.errors
        : null;

    if (errors) {
      // spackle-api PR #3152 renders `{ errors: 'Document not found' }`
      // with status :not_found when the document is genuinely missing.
      return { ok: false, endpointAbsent: false, errors };
    }

    // No `errors` key — this is Rails' default router 404, meaning the
    // route itself is not registered. Fall back to the legacy direct S3
    // URL.
    return { ok: false, endpointAbsent: true };
  }

  return { ok: true, url: response.data && response.data.url };
}

export function pdfDownloadFailureFrom(error) {
  // NEVER endpointAbsent: a 401/403 is a real refusal, and because the
  // interceptor strips `status` from any body lacking an `errors` key, a
  // status-stripped 403 is indistinguishable from a transport failure —
  // silently bypassing that would be a security regression.
  const responseErrors =
    error &&
    error.response &&
    error.response.data &&
    Object.prototype.hasOwnProperty.call(error.response.data, 'errors')
      ? error.response.data.errors
      : null;

  return { ok: false, endpointAbsent: false, errors: responseErrors };
}
